import { Button, Group, Stack, Table, Text } from '@mantine/core';
import { Link, useParams } from 'react-router';
import { useGorev, useIkincilOkuma } from '../api/queries';
import { donemGoster, tarihGoster } from '../lib/format';
import { QueryStatus } from './parts';
import { GorevRozeti, HedefDurumuSatirlari } from './gorev-parts';
import { Bolum, KapiSebebi, KayitDetayi } from '../sozlesme';

/**
 * GÖREV DETAYI (F4·S5·B2 — T10/#2; sayfa sözleşmesi P5 · P6).
 *
 * ⚠️ **SEKME YOK — bölümler alt alta** (P5). Görev · nesne · dönem ızgarası
 * tek kaydırmada durur; çıktıya giden şey **sayfanın kendisidir**.
 *
 * ⚠️ **SALT OKUMA.** Kapanış burada YAPILMAZ: görev **form doldurulunca**
 * kapanır ve bunun ucu yoktur (§22.6a). Ekranda bir "kapat" düğmesi görmek
 * kullanıcıya **olmayan bir yol** vaat ederdi.
 *
 * ✏️ **T3 (türetme paketi — bulgu 93, 17.09.2026): FORM BURADAN AÇILIR.**
 * Görevden forma giden yol yoktu; *Formu doldur* görevin formunu görevle
 * açar — işyeri ve ekipman görevden türetilir, formda sorulmaz. Kapı
 * sunucudan (`doldurmaKapisi`); yazamayan sebep satırı görür (K-63).
 */
export function GorevDetayScreen(): React.JSX.Element {
  const { occurrenceId } = useParams();
  const gorev = useGorev(occurrenceId ?? '');
  // Görevi ATAMA yoluyla açan kişi (şapkasız çalışan) görev listesini okuyamaz;
  // dönüş yolu geldiği yerdir — İşlerim (P6: menüsüz sayfanın dönüşü üzerindedir).
  const gorevListesi = useIkincilOkuma('gorevListesi');
  const veri = gorev.data;

  return (
    <Stack>
      <QueryStatus queries={[gorev]} />
      {veri && (
        <KayitDetayi
          testId="gorev-detay"
          geriYol={gorevListesi.acik ? '/ekipman/gorevler' : '/islerim'}
          geriEtiket={gorevListesi.acik ? 'Periyodik görevlere dön' : 'İşlerime dön'}
          baslik={`${veri.nesne.ad} — ${veri.kalemAd}`}
          eylemler={
            veri.doldurmaKapisi.yazabilir ? (
              <Button
                component={Link}
                to={`/formlar/doldur/${veri.formTemplateId}?gorev=${veri.id}`}
                data-testid="gorev-formu-doldur"
              >
                Formu doldur
              </Button>
            ) : undefined
          }
          ustBilgi={
            <Group gap="xs">
              <Text size="sm" c="dimmed">
                {veri.nesne.workplaceAd} · dönem {donemGoster(veri.donem)}
              </Text>
              <GorevRozeti satir={veri} testId="gorev-detay-rozeti" />
            </Group>
          }
        >
          <Bolum baslik="Görev" testId="gorev-detay-kunye">
            {veri.durum === 'bekliyor' && (
              <KapiSebebi
                durum={veri.doldurmaKapisi}
                nesne="Görevin formunu"
                fiil="doldurur"
                testId="gorev-formu-yazamama-sebebi"
              />
            )}
            <Table variant="vertical" withRowBorders={false}>
              <Table.Tbody>
                <Table.Tr>
                  <Table.Th w={200}>Vade</Table.Th>
                  <Table.Td>{tarihGoster(veri.vadeTarihi)}</Table.Td>
                </Table.Tr>
                <Table.Tr>
                  <Table.Th>Periyot biçimi</Table.Th>
                  <Table.Td>{veri.periyotBicimi === 'kayan' ? 'Kayan' : 'Takvim'}</Table.Td>
                </Table.Tr>
                <Table.Tr>
                  <Table.Th>Hedef</Table.Th>
                  <Table.Td data-testid="gorev-hedef">
                    {veri.hedefTipi === 'kisi'
                      ? (veri.hedefAd ?? 'Kişi hedefi tanımsız')
                      : veri.kumeKaynagi === 'operator_listesi'
                        ? 'Küme — operatör listesi'
                        : 'Küme — şapka + bahçe'}
                    <HedefDurumuSatirlari
                      durum={veri.hedefDurumu}
                      kume={veri.hedefTipi === 'kume'}
                    />
                  </Table.Td>
                </Table.Tr>
                <Table.Tr>
                  <Table.Th>Kapanış</Table.Th>
                  <Table.Td>{tarihGoster(veri.kapanisAt)}</Table.Td>
                </Table.Tr>
                <Table.Tr>
                  <Table.Th>Dolduran</Table.Th>
                  <Table.Td>{veri.dolduranAd ?? '—'}</Table.Td>
                </Table.Tr>
              </Table.Tbody>
            </Table>
            {/* Z4 — "başkası doldurdu" notu YALNIZ kişi hedefinde; küme
                hedefinde kümeden birinin doldurması BEKLENEN davranıştır ve
                şerh anlamını yitirirdi. Sunucu da o alanı küme hedefinde
                `false` döner — ekran ikinci kural yazmıyor, aynı kuralı
                gösteriyor. */}
            {veri.atananDisindaMi && (
              <Text size="sm" c="dimmed" mt="xs" data-testid="gorev-baskasi-doldurdu">
                Bu görevi atanan kişi dışında biri doldurdu.
              </Text>
            )}
          </Bolum>

          <Bolum
            baslik="Dönem ızgarası"
            aciklama="Kaçırılmış dönem bir GEÇMİŞ PERFORMANS kaydıdır, bugünün kapısı değildir (G2-a): boş hücreler kalıcıdır."
            testId="gorev-donem-izgarasi"
          >
            <Table striped>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Dönem</Table.Th>
                  <Table.Th>Vade</Table.Th>
                  <Table.Th>Durum</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {veri.gecmisDonemler.map((donem) => (
                  <Table.Tr key={donem.id} data-testid="gorev-donem-satiri">
                    <Table.Td>{donemGoster(donem.donem)}</Table.Td>
                    <Table.Td>{tarihGoster(donem.vadeTarihi)}</Table.Td>
                    <Table.Td>
                      {/* Vade işareti ızgarada da AYNI yüklemden okunur ve
                          SUNUCUDAN gelir (madde 296): ekran vade ile
                          "bugün"ü kendi karşılaştırmaz — karşılaştırırsa
                          gün sınırı tarayıcının saatine göre ayrışır. */}
                      <GorevRozeti satir={donem} />
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Bolum>
        </KayitDetayi>
      )}
    </Stack>
  );
}
